import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Calendar } from 'primereact/calendar';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import api, { normalize } from '../../services/api';
import AgenteFormDialog from '../../components/AgenteFormDialog';
import { richItemTemplate, richValueTemplate } from '../../components/RichSelectOption';
import '../../components/RichSelectOption.css';
import { BuscaDinamica } from '../../lanz-ui';
import { buildAgenteFilterOptions, buildFilialFilterOptions } from '../../utils/filtroHelpers';
import { parseCnaesList } from '../../utils/cnaeHelpers';
import { expandModeloTexto } from '../../utils/expandModeloTexto';
import './NfseConsulta.css';

const EMPTY_FORM = {
  filialId: 0,
  tomadorAgenteId: null,
  tomadorNaoInformar: false,
  competencia: new Date(),
  valorServico: null,
  descontoIncondicionado: 0,
  descontoCondicionado: 0,
  deducoes: 0,
  aliquotaIss: 0,
  issRetido: false,
  codigoItemServico: '',
  codigoTributacaoNacional: '',
  codigoTributacaoMunicipal: '001',
  discriminacao: '',
  cnaeUtilizado: '',
  codigoNbs: '',
  indOp: '',
  localPrestacaoIbge: '',
  aliquotaIbs: 0,
  aliquotaCbs: 0,
  cstIbs: '',
  cClassTribIbs: '',
  cstCbs: '',
  cClassTribCbs: '',
  valorAjusteBc: 0,
  motivoAjuste: '',
  nfseReferenciaAjusteId: null,
  tipoDocumento: 'Padrao',
  valorPis: 0,
  valorCofins: 0,
  valorInss: 0,
  valorIr: 0,
  valorCsll: 0,
  outrasRetencoes: 0,
};

function money(v) {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

export default function NfseEmissaoPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useRef(null);

  const isAjusteMode = searchParams.get('ajuste') === '1';
  const refIdParam = searchParams.get('refId');
  const editIdParam = searchParams.get('id');
  const editId = editIdParam ? Number(editIdParam) : null;

  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    tipoDocumento: isAjusteMode ? 'Ajuste' : 'Padrao',
    nfseReferenciaAjusteId: refIdParam ? Number(refIdParam) : null,
  }));
  const [editMeta, setEditMeta] = useState(null); // { id, status, mensagemErro, ... }
  const [agentes, setAgentes] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [filiais, setFiliais] = useState([]);
  const [faq, setFaq] = useState([]);
  const [preview, setPreview] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processandoVisible, setProcessandoVisible] = useState(false);
  const [processandoMsg, setProcessandoMsg] = useState('');
  const [novoAgenteVisible, setNovoAgenteVisible] = useState(false);
  const [rtcAberto, setRtcAberto] = useState(isAjusteMode ? 0 : null);
  const [avancadoAberto, setAvancadoAberto] = useState(null);
  const [itensServico, setItensServico] = useState([]);
  const [itensSugeridos, setItensSugeridos] = useState([]);
  const [nbsOptions, setNbsOptions] = useState([]);
  const [indOpOptions, setIndOpOptions] = useState([]);
  const [modelosTexto, setModelosTexto] = useState([]);
  const [modeloId, setModeloId] = useState(null);
  const [modeloDialogVisible, setModeloDialogVisible] = useState(false);
  const [modeloValores, setModeloValores] = useState({});
  const [modeloPreview, setModeloPreview] = useState('');
  const autoSugestaoRef = useRef({ item: '', ibge: '' });

  const setCampo = (campo, valor) => setForm((p) => ({ ...p, [campo]: valor }));

  const carregarAgentes = useCallback(async () => {
    try {
      const { data } = await api.get('/agentes', { params: { tipo: 'Cliente' } });
      setAgentes(normalize(data));
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao carregar tomadores.' });
    }
  }, []);

  const carregarItensServico = useCallback(async (cnaePrincipal, cnaesSecundarios = []) => {
    try {
      const principal = String(cnaePrincipal || '').replace(/\D/g, '');
      const secs = parseCnaesList(cnaesSecundarios).filter((c) => c && c !== principal);
      const todosCnaes = [principal, ...secs].filter(Boolean);

      const [all, nbs, indop, modelos, ...sugRes] = await Promise.all([
        api.get('/catalogos/itens-servico'),
        api.get('/catalogos/nbs').catch(() => ({ data: [] })),
        api.get('/catalogos/indop').catch(() => ({ data: [] })),
        api.get('/modelos-texto').catch(() => ({ data: [] })),
        ...todosCnaes.map((c) =>
          api.get('/catalogos/itens-servico/sugeridos', { params: { cnae: c } }).catch(() => ({ data: [] }))
        ),
      ]);

      const catalogo = normalize(all.data);
      const sugPrincipal = principal ? normalize(sugRes[0]?.data || []) : [];
      const codigosPermitidos = new Set();
      sugRes.forEach((r) => {
        normalize(r?.data || []).forEach((i) => {
          if (i?.codigo) codigosPermitidos.add(i.codigo);
        });
      });

      // Limita à união dos mapas CNAE principal+secundários; se mapa vazio, mantém catálogo (fallback)
      const itens =
        codigosPermitidos.size > 0
          ? catalogo.filter((i) => codigosPermitidos.has(i.codigo))
          : catalogo;

      setItensServico(itens);
      setItensSugeridos(sugPrincipal);
      setNbsOptions(normalize(nbs.data));
      setIndOpOptions(normalize(indop.data));
      setModelosTexto(normalize(modelos.data).filter((m) => m.ativo !== false));
    } catch {
      setItensServico([]);
      setItensSugeridos([]);
    }
  }, []);

  useEffect(() => {
    carregarAgentes();
    (async () => {
      try {
        const [eRes, fRes] = await Promise.all([
          api.get('/empresas/me').catch(() => ({ data: null })),
          api.get('/filiais').catch(() => ({ data: [] })),
        ]);
        const emp = normalize(eRes.data);
        setEmpresa(emp);
        setFiliais(normalize(fRes.data));
        if (!editId && emp?.cnaePrincipal) {
          setForm((p) => (p.cnaeUtilizado ? p : { ...p, cnaeUtilizado: emp.cnaePrincipal }));
        }
        if (!editId && (emp?.codigoIbge || emp?.municipioEmissorIbge)) {
          setForm((p) =>
            p.localPrestacaoIbge
              ? p
              : { ...p, localPrestacaoIbge: emp.municipioEmissorIbge || emp.codigoIbge || '' }
          );
        }
      } catch {
        // lookups opcionais
      }
    })();
    (async () => {
      try {
        const { data } = await api.get('/help/faq', { params: { contexto: 'Emissao' } });
        const lista = normalize(data);
        setFaq([...new Map(lista.map((f) => [f.codigo, f])).values()]);
      } catch {
        setFaq([]);
      }
    })();
  }, [carregarAgentes, editId]);

  // Carrega rascunho / rejeitada para edição
  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const { data } = await api.get(`/nfse/emitidas/${editId}`);
        const n = normalize(data);
        const editaveis = ['Rascunho', 'Rejeitada', 'ErroComunicacao'];
        if (!editaveis.includes(n.status)) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Edição',
            detail: `Notas com status "${n.status}" não podem ser editadas.`,
          });
          navigate('/nfse/emissor');
          return;
        }
        setEditMeta({
          id: n.id,
          status: n.status,
          mensagemErro: n.mensagemAmigavel || n.mensagemErro,
          codigoErro: n.codigoErro,
          comoResolver: n.comoResolver,
          numeroDps: n.numeroDps,
          serieDps: n.serieDps,
        });
        let competencia = new Date();
        if (n.competencia) {
          const parts = String(n.competencia).split(/[-/]/);
          if (parts.length >= 2) {
            const y = Number(parts[0]);
            const m = Number(parts[1]);
            if (y && m) competencia = new Date(y, m - 1, 1);
          }
        }
        setForm((p) => ({
          ...p,
          filialId: n.filialId > 0 ? n.filialId : 0,
          tomadorAgenteId: n.tomadorAgenteId || null,
          tomadorNaoInformar: !n.tomadorAgenteId,
          competencia,
          valorServico: n.valorServicos ?? n.valores?.valorServico ?? null,
          descontoIncondicionado: n.descontoIncondicionado ?? 0,
          descontoCondicionado: n.descontoCondicionado ?? 0,
          deducoes: n.deducoes ?? 0,
          aliquotaIss: n.aliquotaIss ?? 0,
          issRetido: !!n.issRetido,
          codigoItemServico: n.codigoItemServico || '',
          codigoTributacaoNacional: n.codigoTributacaoNacional || '',
          codigoTributacaoMunicipal: n.codigoTributacaoMunicipal || '001',
          discriminacao: n.discriminacao || '',
          cnaeUtilizado: n.cnaeUtilizado || '',
          codigoNbs: n.codigoNbs || '',
          indOp: n.indOp || '',
          localPrestacaoIbge: n.localPrestacaoIbge || '',
          valorPis: n.valorPis ?? 0,
          valorCofins: n.valorCofins ?? 0,
          valorInss: n.valorInss ?? 0,
          valorIr: n.valorIr ?? 0,
          valorCsll: n.valorCsll ?? 0,
          outrasRetencoes: n.outrasRetencoes ?? 0,
          aliquotaIbs: n.aliquotaIbs ?? 0,
          aliquotaCbs: n.aliquotaCbs ?? 0,
          valorAjusteBc: n.valores?.valorAjusteBc ?? 0,
          cstIbs: n.cstIbs || '',
          cClassTribIbs: n.cClassTribIbs || '',
          cstCbs: n.cstCbs || '',
          cClassTribCbs: n.cClassTribCbs || '',
          tipoDocumento: n.tipoDocumento || 'Padrao',
          motivoAjuste: n.motivoAjuste || '',
          nfseReferenciaAjusteId: n.nfseReferenciaAjusteId || null,
        }));
        if (n.status === 'Rejeitada' || n.status === 'ErroComunicacao') {
          setStep(1);
        }
      } catch {
        toast.current?.show({
          severity: 'error',
          summary: 'Edição',
          detail: 'Não foi possível carregar a NFS-e para edição.',
        });
        navigate('/nfse/emissor');
      }
    })();
  }, [editId, navigate]);

  const cnaesPrestador = useMemo(() => {
    const filial = form.filialId > 0 ? (filiais || []).find((f) => f.id === form.filialId) : null;
    const principal = String(filial?.cnaePrincipal || empresa?.cnaePrincipal || '').replace(/\D/g, '');
    const secundarios = parseCnaesList(
      filial?.cnaesSecundariosJson ?? empresa?.cnaesSecundariosJson
    );
    return { principal, secundarios, key: `${principal}|${secundarios.join(',')}` };
  }, [empresa, filiais, form.filialId]);

  const cnaePrestador = cnaesPrestador.principal;

  useEffect(() => {
    // CNAE na DPS vem do cadastro (empresa/filial), não é digitado na emissão
    if (cnaePrestador && form.cnaeUtilizado !== cnaePrestador) {
      setCampo('cnaeUtilizado', cnaePrestador);
    }
  }, [cnaePrestador]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    carregarItensServico(cnaesPrestador.principal, cnaesPrestador.secundarios);
  }, [cnaesPrestador.key, carregarItensServico]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Auto-sugere primeiro item do mapa CNAE se ainda vazio
    if (!form.codigoItemServico && itensSugeridos?.length) {
      setCampo('codigoItemServico', itensSugeridos[0].codigo);
    }
  }, [itensSugeridos]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const item = (form.codigoItemServico || '').trim();
    const ibge = (form.localPrestacaoIbge || '').trim();
    if (!item) return;
    const key = `${item}|${ibge}`;
    if (autoSugestaoRef.current.item === key) return;
    autoSugestaoRef.current.item = key;
    (async () => {
      try {
        const { data } = await api.get('/catalogos/sugestao', {
          params: { itemServico: item, ibge: ibge || undefined, cnae: form.cnaeUtilizado || undefined },
        });
        const s = normalize(data);
        setForm((p) => ({
          ...p,
          codigoNbs: p.codigoNbs || s.codigoNbs || '',
          indOp: p.indOp || s.indOp || '',
          codigoTributacaoNacional: s.codigoTributacaoNacional || p.codigoTributacaoNacional || '',
          aliquotaIss: s.aliquotaIss != null ? Number(s.aliquotaIss) : p.aliquotaIss,
          issRetido: s.issRetidoSugerido ? true : p.issRetido,
        }));
      } catch {
        // sugestão opcional
      }
    })();
  }, [form.codigoItemServico, form.localPrestacaoIbge, form.cnaeUtilizado]);

  useEffect(() => {
    if (!isAjusteMode || !refIdParam) return;
    (async () => {
      try {
        const { data } = await api.get(`/nfse/emitidas/${refIdParam}`);
        const ref = normalize(data);
        setForm((p) => ({
          ...p,
          tipoDocumento: 'Ajuste',
          nfseReferenciaAjusteId: ref.id,
          tomadorAgenteId: ref.tomadorAgenteId ?? p.tomadorAgenteId,
          valorServico: ref.valores?.valorServico ?? ref.valorServicos ?? p.valorServico,
          aliquotaIss: ref.aliquotaIss ?? p.aliquotaIss,
          aliquotaIbs: ref.aliquotaIbs ?? p.aliquotaIbs,
          aliquotaCbs: ref.aliquotaCbs ?? p.aliquotaCbs,
          codigoItemServico: ref.codigoItemServico || p.codigoItemServico,
          discriminacao: ref.discriminacao || p.discriminacao,
          cnaeUtilizado: ref.cnaeUtilizado || p.cnaeUtilizado,
          codigoNbs: ref.codigoNbs || p.codigoNbs,
          indOp: ref.indOp || p.indOp,
          localPrestacaoIbge: ref.localPrestacaoIbge || p.localPrestacaoIbge,
          issRetido: ref.issRetido ?? p.issRetido,
          cstIbs: ref.cstIbs || p.cstIbs,
          cClassTribIbs: ref.cClassTribIbs || p.cClassTribIbs,
          cstCbs: ref.cstCbs || p.cstCbs,
          cClassTribCbs: ref.cClassTribCbs || p.cClassTribCbs,
        }));
      } catch {
        toast.current?.show({
          severity: 'warn',
          summary: 'Referência',
          detail: 'Não foi possível carregar a NFS-e de referência.',
        });
      }
    })();
  }, [isAjusteMode, refIdParam]);

  const filialOptions = useMemo(
    () => buildFilialFilterOptions(empresa, filiais, { incluirTodas: false }),
    [empresa, filiais]
  );

  const agenteOptions = useMemo(
    () =>
      buildAgenteFilterOptions(agentes, {
        // 0 = matriz → todos os agentes da empresa
        filialId: form.filialId > 0 ? form.filialId : null,
        incluirTodos: false,
        modo: 'emissao',
        tipo: 'Cliente',
      }),
    [agentes, form.filialId]
  );

  const itemServicoBuscaOptions = useMemo(() => {
    const sugCodes = new Set((itensSugeridos || []).map((s) => s.codigo));
    const opts = [];
    (itensSugeridos || []).forEach((s) => {
      opts.push({
        id: `sug-${s.codigo}`,
        codigo: s.codigo,
        descricao: `★ ${s.descricao || ''}`,
        sugerido: true,
      });
    });
    (itensServico || []).forEach((i) => {
      if (sugCodes.has(i.codigo)) return;
      opts.push({
        id: i.id || i.codigo,
        codigo: i.codigo,
        descricao: i.descricao || '',
        sugerido: false,
        nbsSugerido: i.nbsSugerido,
        indOpSugerido: i.indOpSugerido,
      });
    });
    return opts;
  }, [itensServico, itensSugeridos]);

  const modeloSelecionado = useMemo(
    () => modelosTexto.find((m) => m.id === modeloId) || null,
    [modelosTexto, modeloId]
  );

  const modeloOptions = useMemo(
    () => (modelosTexto || []).map((m) => ({ label: m.nome, value: m.id })),
    [modelosTexto]
  );

  const aplicarSugestaoItem = useCallback(async (codigo) => {
    if (!codigo) return;
    autoSugestaoRef.current.item = '';
    setCampo('codigoItemServico', codigo);
    setCampo('codigoTributacaoNacional', '');
    setCampo('codigoNbs', '');
    setCampo('indOp', '');
  }, []);

  const abrirModeloDialog = (id) => {
    const mid = id ?? modeloId;
    const modelo = modelosTexto.find((m) => m.id === mid);
    if (!modelo) {
      toast.current?.show({ severity: 'warn', summary: 'Modelo', detail: 'Selecione um modelo de texto.' });
      return;
    }
    setModeloId(mid);
    const vals = {};
    (modelo.variaveis || []).forEach((v) => {
      vals[v.nome] = v.valorPadrao || '';
    });
    setModeloValores(vals);
    const preview = expandModeloTexto(modelo.conteudo, modelo.variaveis, vals, {
      valorServico: form.valorServico,
      aliquotaIss: form.aliquotaIss,
      descontoIncondicionado: form.descontoIncondicionado,
      tomadorNome: tomadorSelecionado?.razaosocial || tomadorSelecionado?.fantasia,
      tomadorDoc: tomadorSelecionado?.cnpjcpf,
    });
    setModeloPreview(preview);
    setModeloDialogVisible(true);
  };

  const atualizarPreviewModelo = (vals) => {
    if (!modeloSelecionado) return;
    setModeloPreview(
      expandModeloTexto(modeloSelecionado.conteudo, modeloSelecionado.variaveis, vals, {
        valorServico: form.valorServico,
        aliquotaIss: form.aliquotaIss,
        descontoIncondicionado: form.descontoIncondicionado,
        tomadorNome: tomadorSelecionado?.razaosocial || tomadorSelecionado?.fantasia,
        tomadorDoc: tomadorSelecionado?.cnpjcpf,
      })
    );
  };

  const aplicarModeloNaDiscriminacao = () => {
    if (!modeloSelecionado) return;
    const faltando = (modeloSelecionado.variaveis || []).filter(
      (v) => v.obrigatoria && !(modeloValores[v.nome] || '').toString().trim()
    );
    if (faltando.length) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Variáveis',
        detail: `Preencha: ${faltando.map((v) => v.nome).join(', ')}`,
      });
      return;
    }
    const texto = expandModeloTexto(
      modeloSelecionado.conteudo,
      modeloSelecionado.variaveis,
      modeloValores,
      {
        valorServico: form.valorServico,
        aliquotaIss: form.aliquotaIss,
        descontoIncondicionado: form.descontoIncondicionado,
        tomadorNome: tomadorSelecionado?.razaosocial || tomadorSelecionado?.fantasia,
        tomadorDoc: tomadorSelecionado?.cnpjcpf,
      }
    );
    setCampo('discriminacao', texto);
    setModeloDialogVisible(false);
    toast.current?.show({ severity: 'success', summary: 'Modelo aplicado', detail: 'Discriminação preenchida.' });
  };

  const tomadorSelecionado = useMemo(
    () => agentes.find((a) => a.id === form.tomadorAgenteId),
    [agentes, form.tomadorAgenteId]
  );

  const requestCalculo = useCallback(async () => {
    if (form.valorServico == null || Number(form.valorServico) < 0) {
      setPreview(null);
      return;
    }
    setCalcLoading(true);
    try {
      const { data } = await api.post('/nfse/emitidas/calculo', {
        valorServico: Number(form.valorServico) || 0,
        descontoIncondicionado: Number(form.descontoIncondicionado) || 0,
        descontoCondicionado: Number(form.descontoCondicionado) || 0,
        deducoes: Number(form.deducoes) || 0,
        aliquotaIss: Number(form.aliquotaIss) || 0,
        issRetido: !!form.issRetido,
        aliquotaIbs: Number(form.aliquotaIbs) || 0,
        aliquotaCbs: Number(form.aliquotaCbs) || 0,
        valorAjusteBc: Number(form.valorAjusteBc) || 0,
        valorPis: Number(form.valorPis) || 0,
        valorCofins: Number(form.valorCofins) || 0,
        valorInss: Number(form.valorInss) || 0,
        valorIr: Number(form.valorIr) || 0,
        valorCsll: Number(form.valorCsll) || 0,
        outrasRetencoes: Number(form.outrasRetencoes) || 0,
      });
      setPreview(normalize(data));
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Cálculo',
        detail: e.response?.data?.message || 'Falha ao calcular preview.',
      });
    } finally {
      setCalcLoading(false);
    }
  }, [form]);

  const buildPayload = (emitirAgora, forcarHomologacao = false) => {
    const comp = form.competencia instanceof Date ? form.competencia : new Date(form.competencia || Date.now());
    return {
      filialId: form.filialId > 0 ? form.filialId : null,
      tomadorAgenteId: form.tomadorNaoInformar ? null : form.tomadorAgenteId,
      tomadorNaoInformar: !!form.tomadorNaoInformar,
      competencia: comp.toISOString(),
      valorServico: Number(form.valorServico) || 0,
      descontoIncondicionado: Number(form.descontoIncondicionado) || 0,
      descontoCondicionado: Number(form.descontoCondicionado) || 0,
      deducoes: Number(form.deducoes) || 0,
      aliquotaIss: Number(form.aliquotaIss) || 0,
      issRetido: !!form.issRetido,
      codigoItemServico: form.codigoItemServico || null,
      codigoTributacaoNacional: form.codigoTributacaoNacional || null,
      codigoTributacaoMunicipal: form.codigoTributacaoMunicipal || '001',
      discriminacao: form.discriminacao || null,
      cnaeUtilizado: cnaePrestador || form.cnaeUtilizado || null,
      codigoNbs: form.codigoNbs || null,
      indOp: form.indOp || null,
      localPrestacaoIbge: form.localPrestacaoIbge || null,
      aliquotaIbs: Number(form.aliquotaIbs) || 0,
      aliquotaCbs: Number(form.aliquotaCbs) || 0,
      cstIbs: form.cstIbs || null,
      cClassTribIbs: form.cClassTribIbs || null,
      cstCbs: form.cstCbs || null,
      cClassTribCbs: form.cClassTribCbs || null,
      valorAjusteBc: Number(form.valorAjusteBc) || 0,
      valorPis: Number(form.valorPis) || 0,
      valorCofins: Number(form.valorCofins) || 0,
      valorInss: Number(form.valorInss) || 0,
      valorIr: Number(form.valorIr) || 0,
      valorCsll: Number(form.valorCsll) || 0,
      outrasRetencoes: Number(form.outrasRetencoes) || 0,
      tipoDocumento: form.tipoDocumento || 'Padrao',
      motivoAjuste: form.tipoDocumento === 'Ajuste' ? form.motivoAjuste : null,
      nfseReferenciaAjusteId: form.tipoDocumento === 'Ajuste' ? form.nfseReferenciaAjusteId : null,
      origem: forcarHomologacao ? 'Homologacao' : 'Manual',
      emitirAgora: !!emitirAgora,
      forcarHomologacao: !!forcarHomologacao,
    };
  };

  const pollStatus = async (id) => {
    setProcessandoVisible(true);
    setProcessandoMsg('Processando e em breve o status será atualizado.');
    let last = null;
    for (let i = 0; i < 20; i += 1) {
      try {
        const { data } = await api.post(`/nfse/emitidas/${id}/consultar-status`);
        last = normalize(data);
        if (last.status && last.status !== 'Processando' && last.status !== 'Validando') break;
      } catch {
        // continua tentando
      }
      await sleep(2500);
    }
    setProcessandoVisible(false);
    return last;
  };

  const validarStep1 = () => {
    if (!form.tomadorNaoInformar && !form.tomadorAgenteId) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Tomador',
        detail: 'Selecione o tomador ou marque “Não informar tomador”.',
      });
      return false;
    }
    if (form.tipoDocumento === 'Ajuste' && !form.motivoAjuste?.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Ajuste',
        detail: 'Informe o motivo da nota de ajuste.',
      });
      return false;
    }
    if (form.tipoDocumento === 'Ajuste' && !form.nfseReferenciaAjusteId) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Ajuste',
        detail: 'Informe a NFS-e de referência do ajuste.',
      });
      return false;
    }
    return true;
  };

  const validarStep2 = () => {
    if (form.valorServico == null || Number(form.valorServico) <= 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Valor',
        detail: 'Informe o valor do serviço.',
      });
      return false;
    }
    if (!form.codigoItemServico?.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Serviço',
        detail: 'Informe o item de serviço.',
      });
      return false;
    }
    if (!form.discriminacao?.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Discriminação',
        detail: 'Informe a discriminação do serviço.',
      });
      return false;
    }
    return true;
  };

  /** Chamado pelo AgenteFormDialog após criar o tomador com sucesso. */
  const aoSalvarNovoAgente = async (created) => {
    await carregarAgentes();
    setCampo('tomadorAgenteId', created.id);
    setCampo('tomadorNaoInformar', false);
    setNovoAgenteVisible(false);
  };

  const submeter = async (emitirAgora, forcarHomologacao = false) => {
    if (!validarStep1() || !validarStep2()) return;

    const executar = async () => {
      setSaving(true);
      try {
        const payload = buildPayload(emitirAgora, forcarHomologacao);
        const { data } = editId
          ? await api.put(`/nfse/emitidas/${editId}`, payload)
          : await api.post('/nfse/emitidas', payload);
        const created = normalize(data);
        toast.current?.show({
          severity: 'success',
          summary: forcarHomologacao ? 'Homologação' : emitirAgora ? 'Emissão' : 'Rascunho',
          detail: forcarHomologacao
            ? 'Pré-NF enviada ao ambiente de Homologação.'
            : emitirAgora
              ? 'NFS-e enviada para processamento.'
              : editId
                ? 'Rascunho atualizado.'
                : 'Rascunho salvo.',
        });

        if (emitirAgora && (created.status === 'Processando' || created.status === 'Validando' || created.status === 'Rejeitada' || created.status === 'Autorizada')) {
          let final = created;
          if (created.status === 'Processando' || created.status === 'Validando') {
            final = await pollStatus(created.id);
          }
          if (final?.status === 'Autorizada') {
            toast.current?.show({
              severity: 'success',
              summary: forcarHomologacao ? 'Homologação autorizada' : 'Autorizada',
              detail: `NFS-e ${final.numeroNfse || created.id} autorizada${forcarHomologacao ? ' (homologação)' : ''}.`,
            });
          } else if (final?.status === 'Rejeitada' || final?.status === 'ErroComunicacao') {
            toast.current?.show({
              severity: 'error',
              summary: final.status,
              detail: final.mensagemAmigavel || final.mensagemErro || 'Falha na emissão.',
            });
          } else if (final) {
            toast.current?.show({
              severity: 'info',
              summary: 'Status',
              detail: `Status atual: ${final.status}`,
            });
          }
        }
        navigate('/nfse/emissor');
      } catch (e) {
        toast.current?.show({
          severity: 'error',
          summary: 'Erro',
          detail: e.response?.data?.message || 'Falha ao salvar/emitir.',
        });
      } finally {
        setSaving(false);
        setProcessandoVisible(false);
      }
    };

    if (!emitirAgora) {
      await executar();
      return;
    }

    if (forcarHomologacao) {
      confirmDialog({
        message:
          'Gera uma pré-NF no Ambiente Nacional de Homologação (teste), sem usar Produção. Continuar?',
        header: 'Emitir em Homologação',
        icon: 'pi pi-info-circle',
        acceptLabel: 'Sim, homologar',
        rejectLabel: 'Cancelar',
        accept: () => {
          setProcessandoMsg('Enviando à Homologação…');
          setProcessandoVisible(true);
          executar();
        },
      });
      return;
    }

    confirmDialog({
      message:
        'Confirma o envio desta NFS-e ao Ambiente Nacional? Usa o ambiente fiscal cadastrado na empresa/filial (Homologação ou Produção).',
      header: 'Confirmar emissão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim, emitir',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: () => {
        setProcessandoMsg('Emitindo NFS-e…');
        setProcessandoVisible(true);
        executar();
      },
    });
  };

  const titulo = editId
    ? `Editar NFS-e #${editId}${editMeta?.status ? ` (${editMeta.status})` : ''}`
    : form.tipoDocumento === 'Ajuste'
      ? 'Nota de ajuste IBS/CBS'
      : 'Nova NFSe';

  return (
    <div className="nfse-emissao-page">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="nfse-emissao-header">
        <h1>{titulo}</h1>
        <Button
          label="Voltar à consulta"
          icon="pi pi-arrow-left"
          className="p-button-outlined"
          onClick={() => navigate('/nfse/emissor')}
        />
      </div>

      <div className="nfse-emissao-layout">
        <div className="nfse-emissao-card">
          <div className="nfse-emissao-steps">
            <span className={`nfse-emissao-step${step === 1 ? ' active' : ''}`}>1. Empresa e tomador</span>
            <span className={`nfse-emissao-step${step === 2 ? ' active' : ''}`}>2. Serviço e valores</span>
            <span className={`nfse-emissao-step${step === 3 ? ' active' : ''}`}>3. Revisão e emissão</span>
          </div>

          {form.tipoDocumento === 'Ajuste' && (
            <div className="nfse-emissao-ajuste-banner">
              Modo ajuste — referência #{form.nfseReferenciaAjusteId || '—'}
            </div>
          )}

          {editMeta?.mensagemErro && (
            <div
              className="nfse-emissao-ajuste-banner"
              style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}
            >
              <strong>Último erro ({editMeta.status}):</strong> {editMeta.mensagemErro}
              {editMeta.comoResolver ? (
                <>
                  <br />
                  <span style={{ fontWeight: 500 }}>Como resolver: {editMeta.comoResolver}</span>
                </>
              ) : null}
              {editMeta.serieDps || editMeta.numeroDps ? (
                <>
                  <br />
                  <span style={{ opacity: 0.85 }}>
                    DPS {editMeta.serieDps || '—'}/{editMeta.numeroDps || '—'} (mantido na reemissão)
                  </span>
                </>
              ) : null}
            </div>
          )}

          {step === 1 && (
            <div className="nfse-emissao-step-body">
              <h2>Empresa e tomador</h2>
              <div className="nfse-emissao-field nfse-emissao-field-full">
                <label>Empresa / Filial</label>
                <Dropdown
                  value={form.filialId}
                  options={filialOptions}
                  onChange={(e) => {
                    setCampo('filialId', e.value);
                    setCampo('tomadorAgenteId', null);
                  }}
                  optionLabel="label"
                  filter
                  filterBy="search,label,razao,fantasia,cnpjcpf"
                  itemTemplate={richItemTemplate}
                  valueTemplate={richValueTemplate}
                  panelClassName="rich-select-panel"
                  className="w-full"
                  placeholder="Selecione a empresa / filial"
                />
              </div>
              <div className="nfse-emissao-field nfse-emissao-field-full">
                <label>Tomador</label>
                <Dropdown
                  value={form.tomadorAgenteId}
                  options={agenteOptions}
                  onChange={(e) => {
                    setCampo('tomadorAgenteId', e.value);
                    setCampo('tomadorNaoInformar', false);
                  }}
                  optionLabel="label"
                  placeholder="Selecione o tomador"
                  filter
                  filterBy="search,label,razao,fantasia,cnpjcpf"
                  itemTemplate={richItemTemplate}
                  valueTemplate={richValueTemplate}
                  panelClassName="rich-select-panel"
                  disabled={form.tomadorNaoInformar}
                  emptyMessage="Nenhum cliente disponível para esta empresa/filial"
                  className="w-full"
                  showClear
                />
              </div>
              <div className="nfse-emissao-check-row">
                <Checkbox
                  inputId="semTomador"
                  checked={form.tomadorNaoInformar}
                  onChange={(e) => {
                    setCampo('tomadorNaoInformar', e.checked);
                    if (e.checked) setCampo('tomadorAgenteId', null);
                  }}
                />
                <label htmlFor="semTomador">Não informar tomador</label>
              </div>
              <div className="nfse-emissao-inline-actions">
                <Button
                  label="Novo cliente"
                  icon="pi pi-user-plus"
                  className="p-button-outlined"
                  disabled={form.tomadorNaoInformar}
                  onClick={() => setNovoAgenteVisible(true)}
                />
              </div>

              {form.tipoDocumento === 'Ajuste' && (
                <>
                  <div className="nfse-emissao-field">
                    <label>Motivo do ajuste *</label>
                    <InputTextarea
                      value={form.motivoAjuste}
                      onChange={(e) => setCampo('motivoAjuste', e.target.value)}
                      rows={3}
                      autoResize
                      className="w-full"
                    />
                  </div>
                  <div className="nfse-emissao-field">
                    <label>ID NFS-e referência</label>
                    <InputNumber
                      value={form.nfseReferenciaAjusteId}
                      onValueChange={(e) => setCampo('nfseReferenciaAjusteId', e.value)}
                      useGrouping={false}
                      className="w-full"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="nfse-emissao-step-body">
              <h2>Serviço e valores</h2>
              <div className="nfse-emissao-grid">
                <div className="nfse-emissao-field">
                  <label>Competência *</label>
                  <Calendar
                    value={form.competencia}
                    onChange={(e) => setCampo('competencia', e.value || new Date())}
                    view="month"
                    dateFormat="mm/yy"
                    locale="pt"
                    showIcon
                    className="w-full"
                  />
                </div>
                <div className="nfse-emissao-field nfse-emissao-field-full">
                  <label>Código do serviço (LC 116) *</label>
                  <BuscaDinamica
                    options={itemServicoBuscaOptions}
                    defaultValue={form.codigoItemServico || ''}
                    campos={[
                      { key: 'codigo', label: 'Código' },
                      { key: 'descricao', label: 'Descrição' },
                    ]}
                    placeholder="Buscar código ou descrição — preenche NBS, IndOp e alíquota…"
                    onSelect={(item) => {
                      if (!item?.codigo) {
                        setCampo('codigoItemServico', '');
                        return;
                      }
                      aplicarSugestaoItem(item.codigo);
                    }}
                  />
                  <small style={{ color: '#64748b' }}>
                    Lista limitada ao CNAE principal
                    {cnaePrestador ? ` (${cnaePrestador})` : ''}
                    {cnaesPrestador.secundarios?.length
                      ? ` + ${cnaesPrestador.secundarios.length} secundário(s)`
                      : ''}
                    . ★ = itens do CNAE principal.
                    {!cnaePrestador && !cnaesPrestador.secundarios?.length
                      ? ' Cadastre o CNAE na empresa/filial.'
                      : ''}
                  </small>
                </div>
                <div className="nfse-emissao-field">
                  <label>Cód. tributação municipal (cTribMun)</label>
                  <InputText
                    value={form.codigoTributacaoMunicipal || ''}
                    onChange={(e) => setCampo('codigoTributacaoMunicipal', e.target.value.replace(/\D/g, '').slice(0, 3))}
                    placeholder="001"
                    maxLength={3}
                    className="w-full"
                  />
                  <small style={{ color: '#64748b' }}>
                    Complementar da prefeitura (3 dígitos). Em São Paulo as NFS-e da LANZ usam 001.
                  </small>
                </div>
                <div className="nfse-emissao-field">
                  <label>Valor do serviço *</label>
                  <InputNumber
                    value={form.valorServico}
                    onValueChange={(e) => setCampo('valorServico', e.value)}
                    onBlur={requestCalculo}
                    mode="currency"
                    currency="BRL"
                    locale="pt-BR"
                    className="w-full"
                  />
                </div>
                <div className="nfse-emissao-field">
                  <label>Desconto incondicionado</label>
                  <InputNumber
                    value={form.descontoIncondicionado}
                    onValueChange={(e) => setCampo('descontoIncondicionado', e.value ?? 0)}
                    onBlur={requestCalculo}
                    mode="currency"
                    currency="BRL"
                    locale="pt-BR"
                    className="w-full"
                  />
                </div>
                <div className="nfse-emissao-field">
                  <label>Desconto condicionado</label>
                  <InputNumber
                    value={form.descontoCondicionado}
                    onValueChange={(e) => setCampo('descontoCondicionado', e.value ?? 0)}
                    onBlur={requestCalculo}
                    mode="currency"
                    currency="BRL"
                    locale="pt-BR"
                    className="w-full"
                  />
                </div>
                <div className="nfse-emissao-field">
                  <label>Alíquota ISS (%)</label>
                  <InputNumber
                    value={form.aliquotaIss}
                    onValueChange={(e) => setCampo('aliquotaIss', e.value ?? 0)}
                    onBlur={requestCalculo}
                    minFractionDigits={2}
                    maxFractionDigits={4}
                    className="w-full"
                  />
                </div>
                <div className="nfse-emissao-field nfse-emissao-check-field">
                  <div className="nfse-emissao-check-row">
                    <Checkbox
                      inputId="issRetido"
                      checked={form.issRetido}
                      onChange={(e) => {
                        setCampo('issRetido', e.checked);
                        setTimeout(requestCalculo, 0);
                      }}
                    />
                    <label htmlFor="issRetido">ISS retido</label>
                  </div>
                </div>
                <div className="nfse-emissao-field">
                  <label>NBS</label>
                  <BuscaDinamica
                    options={(nbsOptions || []).map((n) => ({
                      id: n.id || n.codigo,
                      codigo: n.codigo,
                      descricao: n.descricao || '',
                    }))}
                    defaultValue={form.codigoNbs || ''}
                    campos={[
                      { key: 'codigo', label: 'Código' },
                      { key: 'descricao', label: 'Descrição' },
                    ]}
                    placeholder="Sugestão automática — pode alterar"
                    onSelect={(item) => setCampo('codigoNbs', item?.codigo || '')}
                  />
                </div>
                <div className="nfse-emissao-field">
                  <label>IndOp</label>
                  <BuscaDinamica
                    options={(indOpOptions || []).map((n) => ({
                      id: n.id || n.codigo,
                      codigo: n.codigo,
                      descricao: n.descricao || '',
                    }))}
                    defaultValue={form.indOp || ''}
                    campos={[
                      { key: 'codigo', label: 'Código' },
                      { key: 'descricao', label: 'Descrição' },
                    ]}
                    placeholder="Sugestão automática — Anexo VII"
                    onSelect={(item) => setCampo('indOp', item?.codigo || '')}
                  />
                </div>
                <div className="nfse-emissao-field">
                  <label>Local prestação (IBGE)</label>
                  <InputText
                    value={form.localPrestacaoIbge}
                    onChange={(e) => {
                      autoSugestaoRef.current.item = '';
                      setCampo('localPrestacaoIbge', e.target.value);
                    }}
                    className="w-full"
                  />
                </div>
                <div className="nfse-emissao-field">
                  <label>CNAE do prestador</label>
                  <InputText value={cnaePrestador || '—'} disabled className="w-full" />
                  <small style={{ color: '#64748b' }}>
                    Vai na NFS-e; vem do cadastro da empresa/filial (não se edita aqui).
                  </small>
                </div>
                <div className="nfse-emissao-field nfse-emissao-field-full">
                  <label>Discriminação *</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    <Dropdown
                      value={modeloId}
                      options={modeloOptions}
                      onChange={(e) => setModeloId(e.value)}
                      placeholder="Modelo de texto…"
                      showClear
                      style={{ minWidth: 220, flex: 1 }}
                    />
                    <Button
                      type="button"
                      label="Usar modelo"
                      icon="pi pi-file"
                      className="p-button-outlined"
                      disabled={!modeloId}
                      onClick={() => abrirModeloDialog(modeloId)}
                    />
                  </div>
                  <InputTextarea
                    value={form.discriminacao}
                    onChange={(e) => setCampo('discriminacao', e.target.value)}
                    rows={7}
                    autoResize
                    className="w-full nfse-emissao-discriminacao"
                  />
                </div>
              </div>

              <Accordion
                className="nfse-emissao-rtc"
                activeIndex={avancadoAberto}
                onTabChange={(e) => setAvancadoAberto(e.index)}
              >
                <AccordionTab header="Avançado — retenções federais (PIS / COFINS / CSLL / IR / INSS)">
                  <p style={{ color: '#64748b', marginTop: 0 }}>
                    Por padrão ficam zeradas (D14). Informe valores só quando o tomador exigir retenção na fonte — o líquido é recalculado automaticamente.
                  </p>
                  <div className="nfse-emissao-grid">
                    {[
                      ['valorPis', 'PIS'],
                      ['valorCofins', 'COFINS'],
                      ['valorCsll', 'CSLL'],
                      ['valorIr', 'IR'],
                      ['valorInss', 'INSS'],
                      ['outrasRetencoes', 'Outras retenções'],
                    ].map(([campo, label]) => (
                      <div key={campo} className="nfse-emissao-field">
                        <label>{label}</label>
                        <InputNumber
                          value={form[campo]}
                          onValueChange={(e) => setCampo(campo, e.value ?? 0)}
                          onBlur={requestCalculo}
                          mode="currency"
                          currency="BRL"
                          locale="pt-BR"
                          className="w-full"
                        />
                      </div>
                    ))}
                    <div className="nfse-emissao-field">
                      <label>Deduções</label>
                      <InputNumber
                        value={form.deducoes}
                        onValueChange={(e) => setCampo('deducoes', e.value ?? 0)}
                        onBlur={requestCalculo}
                        mode="currency"
                        currency="BRL"
                        locale="pt-BR"
                        className="w-full"
                      />
                    </div>
                  </div>
                </AccordionTab>
              </Accordion>

              <Accordion
                className="nfse-emissao-rtc"
                activeIndex={rtcAberto}
                onTabChange={(e) => setRtcAberto(e.index)}
              >
                <AccordionTab header="Avançado RTC (IBS / CBS)">
                  <div className="nfse-emissao-grid">
                    <div className="nfse-emissao-field">
                      <label>Alíquota IBS (%)</label>
                      <InputNumber
                        value={form.aliquotaIbs}
                        onValueChange={(e) => setCampo('aliquotaIbs', e.value ?? 0)}
                        onBlur={requestCalculo}
                        minFractionDigits={2}
                        maxFractionDigits={4}
                        className="w-full"
                      />
                    </div>
                    <div className="nfse-emissao-field">
                      <label>Alíquota CBS (%)</label>
                      <InputNumber
                        value={form.aliquotaCbs}
                        onValueChange={(e) => setCampo('aliquotaCbs', e.value ?? 0)}
                        onBlur={requestCalculo}
                        minFractionDigits={2}
                        maxFractionDigits={4}
                        className="w-full"
                      />
                    </div>
                    <div className="nfse-emissao-field">
                      <label>CST IBS</label>
                      <InputText value={form.cstIbs} onChange={(e) => setCampo('cstIbs', e.target.value)} className="w-full" />
                    </div>
                    <div className="nfse-emissao-field">
                      <label>cClassTrib IBS</label>
                      <InputText
                        value={form.cClassTribIbs}
                        onChange={(e) => setCampo('cClassTribIbs', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="nfse-emissao-field">
                      <label>CST CBS</label>
                      <InputText value={form.cstCbs} onChange={(e) => setCampo('cstCbs', e.target.value)} className="w-full" />
                    </div>
                    <div className="nfse-emissao-field">
                      <label>cClassTrib CBS</label>
                      <InputText
                        value={form.cClassTribCbs}
                        onChange={(e) => setCampo('cClassTribCbs', e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div className="nfse-emissao-field">
                      <label>Valor ajuste BC</label>
                      <InputNumber
                        value={form.valorAjusteBc}
                        onValueChange={(e) => setCampo('valorAjusteBc', e.value ?? 0)}
                        onBlur={requestCalculo}
                        mode="currency"
                        currency="BRL"
                        locale="pt-BR"
                        className="w-full"
                      />
                    </div>
                  </div>
                </AccordionTab>
              </Accordion>

              <div className="nfse-emissao-preview">
                <div className="nfse-emissao-preview-head">
                  <h3>Preview do cálculo</h3>
                  <Button
                    label="Recalcular"
                    icon="pi pi-refresh"
                    className="p-button-text p-button-sm"
                    loading={calcLoading}
                    onClick={requestCalculo}
                  />
                </div>
                {preview ? (
                  <ul>
                    <li>Base de cálculo: {money(preview.baseCalculo)}</li>
                    <li>Valor ISS: {money(preview.valorIss)}{form.issRetido ? ' (retido)' : ''}</li>
                    <li>
                      Retenções: PIS {money(form.valorPis)} · COFINS {money(form.valorCofins)} · CSLL{' '}
                      {money(form.valorCsll)} · IR {money(form.valorIr)} · INSS {money(form.valorInss)}
                    </li>
                    <li>Base IBS: {money(preview.baseCalculoIbs)} · IBS: {money(preview.valorIbs)}</li>
                    <li>Base CBS: {money(preview.baseCalculoCbs)} · CBS: {money(preview.valorCbs)}</li>
                    <li>Valor líquido: {money(preview.valorLiquido)}</li>
                  </ul>
                ) : (
                  <p>Informe o valor e recalcule para ver o preview.</p>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="nfse-emissao-step-body">
              <h2>Revisão</h2>
              <ul className="nfse-emissao-review">
                <li>
                  <strong>Tomador:</strong>{' '}
                  {form.tomadorNaoInformar
                    ? 'Não informado'
                    : tomadorSelecionado?.razaosocial || tomadorSelecionado?.fantasia || '—'}
                </li>
                {form.tipoDocumento === 'Ajuste' && (
                  <>
                    <li><strong>Tipo:</strong> Ajuste</li>
                    <li><strong>Referência:</strong> #{form.nfseReferenciaAjusteId}</li>
                    <li><strong>Motivo:</strong> {form.motivoAjuste || '—'}</li>
                  </>
                )}
                <li><strong>Competência:</strong> {form.competencia instanceof Date
                  ? `${String(form.competencia.getMonth() + 1).padStart(2, '0')}/${form.competencia.getFullYear()}`
                  : '—'}</li>
                <li><strong>Valor serviço:</strong> {money(form.valorServico)}</li>
                <li><strong>Item serviço:</strong> {form.codigoItemServico || '—'}{form.codigoTributacaoMunicipal ? ` / mun. ${form.codigoTributacaoMunicipal}` : ''}</li>
                <li><strong>CNAE / NBS / IndOp:</strong> {[cnaePrestador || form.cnaeUtilizado, form.codigoNbs, form.indOp].filter(Boolean).join(' · ') || '—'}</li>
                <li><strong>Local IBGE:</strong> {form.localPrestacaoIbge || '—'}</li>
                <li><strong>ISS:</strong> {form.aliquotaIss}%{form.issRetido ? ' (retido)' : ''}</li>
                <li><strong>Discriminação:</strong> {form.discriminacao || '—'}</li>
                {preview && (
                  <li><strong>Valor líquido (preview):</strong> {money(preview.valorLiquido)}</li>
                )}
              </ul>
            </div>
          )}

          <div className="nfse-emissao-actions">
            {step > 1 ? (
              <Button label="Voltar" className="p-button-text" onClick={() => setStep((s) => s - 1)} />
            ) : (
              <Button label="Cancelar" className="p-button-text" onClick={() => navigate('/nfse/emissor')} />
            )}
            {step < 3 ? (
              <Button
                label="Continuar"
                icon="pi pi-arrow-right"
                iconPos="right"
                onClick={() => {
                  if (step === 1 && !validarStep1()) return;
                  if (step === 2) {
                    if (!validarStep2()) return;
                    requestCalculo();
                  }
                  setStep((s) => s + 1);
                }}
              />
            ) : (
              <>
                <Button
                  label="Salvar rascunho"
                  icon="pi pi-save"
                  className="p-button-outlined"
                  loading={saving}
                  onClick={() => submeter(false)}
                />
                <Button
                  label="Homologação"
                  icon="pi pi-verified"
                  className="p-button-outlined p-button-help"
                  loading={saving}
                  tooltip="Gera pré-NF no Ambiente Nacional de Homologação (teste)"
                  tooltipOptions={{ position: 'top' }}
                  onClick={() => submeter(true, true)}
                />
                <Button
                  label="Emitir"
                  icon="pi pi-send"
                  loading={saving}
                  onClick={() => submeter(true, false)}
                />
              </>
            )}
          </div>
        </div>

        <aside className="nfse-emissao-faq">
          <h3>Ajuda</h3>
          {faq.length === 0 && <p className="nfse-emissao-faq-empty">Nenhuma FAQ para este contexto.</p>}
          {faq.map((item) => (
            <details key={item.id || item.codigo} className="nfse-emissao-faq-item">
              <summary>{item.pergunta}</summary>
              <p>{item.resposta}</p>
            </details>
          ))}
        </aside>
      </div>

      <AgenteFormDialog
        visible={novoAgenteVisible}
        onHide={() => setNovoAgenteVisible(false)}
        onSaved={aoSalvarNovoAgente}
        toastRef={toast}
        defaultTipo="Cliente"
        defaultFilialId={form.filialId}
      />

      <Dialog
        header="Processando"
        visible={processandoVisible}
        closable={false}
        style={{ width: 360 }}
        onHide={() => {}}
      >
        <div className="nfse-emissao-processando">
          <ProgressSpinner style={{ width: 48, height: 48 }} strokeWidth="4" />
          <p>{processandoMsg}</p>
        </div>
      </Dialog>

      <Dialog
        header={modeloSelecionado ? `Modelo: ${modeloSelecionado.nome}` : 'Modelo de texto'}
        visible={modeloDialogVisible}
        style={{ width: 640 }}
        breakpoints={{ '720px': '95vw' }}
        onHide={() => setModeloDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Cancelar" className="p-button-text" onClick={() => setModeloDialogVisible(false)} />
            <Button label="Aplicar na discriminação" icon="pi pi-check" onClick={aplicarModeloNaDiscriminacao} />
          </div>
        }
      >
        {(modeloSelecionado?.variaveis || []).length === 0 ? (
          <p style={{ color: '#64748b' }}>Este modelo não tem variáveis tipadas — o texto será aplicado como está (placeholders de contexto da NF ainda são expandíveis).</p>
        ) : (
          <div className="nfse-emissao-grid">
            {(modeloSelecionado.variaveis || []).map((v) => (
              <div key={v.nome} className="nfse-emissao-field nfse-emissao-field-full">
                <label>
                  {v.nome} ({v.tipo || 'Texto'}){v.obrigatoria ? ' *' : ''}
                </label>
                <InputText
                  value={modeloValores[v.nome] || ''}
                  placeholder={v.valorPadrao || `{{${v.nome}}}`}
                  onChange={(e) => {
                    const next = { ...modeloValores, [v.nome]: e.target.value };
                    setModeloValores(next);
                    atualizarPreviewModelo(next);
                  }}
                  className="w-full"
                />
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <strong>Preview</strong>
          <pre style={{ whiteSpace: 'pre-wrap', background: '#f8fafc', padding: 12, borderRadius: 8, marginTop: 8 }}>
            {modeloPreview || '—'}
          </pre>
        </div>
      </Dialog>
    </div>
  );
}
